/**
 * 무료 견적서 만들기 — 품목을 넣으면 인쇄·PDF 로 낼 수 있는 견적서가 바로 나온다.
 *
 * **수익 라인이 아니라 유입 엔진이다**
 * ([목표 §4-b](../../../../docs/goals/2027-06-30-revenue-10m-krw.md)). 현장 사업자가 실제로
 * 검색하는 `견적서 양식` 자리를 무료로 가져가고, **사진·영수증으로 자동 작성하는 것**만
 * 유료(L1)로 남긴다 — 아래 유료 안내 카드가 그 경계다.
 *
 * 신뢰 조건은 퇴사 타임라인과 같다: 로그인 없음 · **서버 전송 없음**(localStorage 만) ·
 * 계산은 전부 브라우저 안. 거래처 금액이 남의 서버로 가지 않는다는 것이 이 도구의 값이다.
 *
 * 인쇄는 `window.print()` 하나로 낸다 — PDF 라이브러리를 넣지 않는다. 브라우저의 "PDF 로
 * 저장"이 이미 그 일을 하고, 번들만 무거워진다. 인쇄용 스타일은 `styles.ts` 의 `@media print`.
 */
import { useEffect, useMemo, useState } from 'react'
import { Badge, Button, Card, Container, Text } from '@cottonray/ui-web'
import {
  computeTotals,
  DOC_PRESETS,
  formatWon,
  lineAmount,
  usableItems,
  validUntil,
  type DocKind,
  type QuoteItem,
  type VatMode,
} from './quoteRules'

const storageKey = (kind: DocKind): string => `doc-builder-${kind}-v1`

interface QuoteState {
  /** 공급자(우리) */
  from: string
  /** 공급받는 자(거래처) */
  to: string
  title: string
  issuedOn: string
  vat: VatMode
  items: QuoteItem[]
  note: string
}

const EMPTY_ITEM: QuoteItem = { name: '', quantity: 1, unitPrice: 0 }

function today(): string {
  return new Date().toISOString().slice(0, 10)
}

const DEFAULTS: QuoteState = {
  from: '',
  to: '',
  title: '견적서',
  issuedOn: today(),
  vat: 'exclusive',
  items: [{ ...EMPTY_ITEM }, { ...EMPTY_ITEM }, { ...EMPTY_ITEM }],
  note: '',
}

function load(kind: DocKind): QuoteState {
  try {
    const raw = localStorage.getItem(storageKey(kind))
    const base = { ...DEFAULTS, title: DOC_PRESETS[kind].title }
    if (raw === null) return base
    const saved = JSON.parse(raw) as Partial<QuoteState>
    // 발행일은 저장값을 쓰지 않는다 — 어제 만든 견적서를 오늘 열면 날짜가 틀린 채로 인쇄된다
    return { ...base, ...saved, issuedOn: today() }
  } catch {
    return { ...DEFAULTS, title: DOC_PRESETS[kind].title }
  }
}

export function QuoteBuilder({ kind = 'quote' }: { kind?: DocKind } = {}) {
  const preset = DOC_PRESETS[kind]
  const [state, setState] = useState<QuoteState>(() => load(kind))

  useEffect(() => {
    try {
      localStorage.setItem(storageKey(kind), JSON.stringify(state))
    } catch {
      // 사파리 프라이빗 모드 등 — 저장 실패가 도구를 막지는 않는다
    }
  }, [state, kind])

  const rows = useMemo(() => usableItems(state.items), [state.items])
  const totals = useMemo(() => computeTotals(state.items, state.vat), [state.items, state.vat])
  const until = useMemo(() => validUntil(state.issuedOn), [state.issuedOn])

  function setItem(index: number, patch: Partial<QuoteItem>): void {
    setState((prev) => ({
      ...prev,
      items: prev.items.map((item, i) => (i === index ? { ...item, ...patch } : item)),
    }))
  }

  return (
    <>
      <section className="qb-hero">
        <Container>
          <Badge tone="accent">무료 · 가입 없음</Badge>
          <Text variant="display" className="qb-hero__title">
            {preset.heading}
          </Text>
          <Text tone="muted" className="qb-hero__lead">
            {preset.lead} 인쇄하거나 PDF 로 저장하세요.
            <strong> 입력한 내용은 이 브라우저에만 남고 어디로도 전송되지 않습니다.</strong>
          </Text>
        </Container>
      </section>

      <section className="qb-body">
        <Container>
          {/* ── 입력 (인쇄에서 빠진다) ── */}
          <Card className="qb-form qb-noprint">
            <div className="qb-grid">
              <label>
                <Text variant="label">공급자 (내 상호)</Text>
                <input
                  value={state.from}
                  onChange={(e) => setState({ ...state, from: e.target.value })}
                  placeholder="예: 코튼레이 청소"
                />
              </label>
              <label>
                <Text variant="label">{preset.counterpartyLabel}</Text>
                <input
                  value={state.to}
                  onChange={(e) => setState({ ...state, to: e.target.value })}
                  placeholder="예: ○○빌딩 관리사무소"
                />
              </label>
              <label>
                <Text variant="label">문서 제목</Text>
                <input value={state.title} onChange={(e) => setState({ ...state, title: e.target.value })} />
              </label>
              <label>
                <Text variant="label">발행일</Text>
                <input
                  type="date"
                  value={state.issuedOn}
                  onChange={(e) => setState({ ...state, issuedOn: e.target.value })}
                />
              </label>
              <label>
                <Text variant="label">부가세</Text>
                <select
                  value={state.vat}
                  onChange={(e) => setState({ ...state, vat: e.target.value as VatMode })}
                >
                  <option value="exclusive">별도 (단가에 미포함, 10% 추가)</option>
                  <option value="inclusive">포함 (단가에 이미 포함)</option>
                  <option value="none">없음 (면세·간이과세)</option>
                </select>
              </label>
            </div>

            <Text variant="label" className="qb-items__label">
              품목
            </Text>
            <div className="qb-items">
              {state.items.map((item, index) => (
                <div className="qb-item" key={index}>
                  <input
                    className="qb-item__name"
                    value={item.name}
                    onChange={(e) => setItem(index, { name: e.target.value })}
                    placeholder="품목·작업 내용"
                  />
                  {/* **0 일 때 포커스하면 전체를 고른다** — 안 그러면 사용자가 0 을 먼저 지워야
                      하고, 급하게 치면 `0150000` 같은 값이 된다 (2026-08-25 실사용 확인) */}
                  <input
                    className="qb-item__num"
                    type="number"
                    min={0}
                    value={item.quantity}
                    onFocus={(e) => {
                      if (Number(e.target.value) === 0) e.target.select()
                    }}
                    onChange={(e) => setItem(index, { quantity: Number(e.target.value) })}
                    aria-label="수량"
                  />
                  <input
                    className="qb-item__num"
                    type="number"
                    min={0}
                    step={100}
                    value={item.unitPrice}
                    onFocus={(e) => {
                      if (Number(e.target.value) === 0) e.target.select()
                    }}
                    onChange={(e) => setItem(index, { unitPrice: Number(e.target.value) })}
                    aria-label="단가"
                  />
                  <span className="qb-item__amount">{formatWon(lineAmount(item))}</span>
                </div>
              ))}
            </div>

            <div className="qb-actions">
              <Button
                variant="secondary"
                onClick={() => setState({ ...state, items: [...state.items, { ...EMPTY_ITEM }] })}
              >
                줄 추가
              </Button>
              <Button onClick={() => window.print()} disabled={rows.length === 0}>
                인쇄 · PDF 로 저장
              </Button>
            </div>

            <label className="qb-note">
              <Text variant="label">비고 (선택)</Text>
              <textarea
                value={state.note}
                onChange={(e) => setState({ ...state, note: e.target.value })}
                placeholder="예: 자재비 별도, 작업 시작 전 50% 선금"
                rows={2}
              />
            </label>
          </Card>

          {/* ── 미리보기 = 인쇄되는 문서 그대로 ── */}
          <Card className="qb-doc">
            <div className="qb-doc__head">
              <Text variant="title">{state.title.trim() === '' ? preset.title : state.title}</Text>
              <div className="qb-doc__meta">
                <span>발행일 {state.issuedOn}</span>
                {preset.showValidUntil && until !== '' ? <span>유효기간 {until} 까지</span> : null}
              </div>
            </div>

            <div className="qb-doc__parties">
              <div>
                <Text variant="label">공급자</Text>
                <Text>{state.from.trim() === '' ? '—' : state.from}</Text>
              </div>
              <div>
                <Text variant="label">공급받는 자</Text>
                <Text>{state.to.trim() === '' ? '—' : state.to}</Text>
              </div>
            </div>

            {rows.length === 0 ? (
              <Text tone="muted" className="qb-doc__empty">
                품목을 넣으면 여기에 {preset.title}가 그려집니다.
              </Text>
            ) : (
              <table className="qb-table">
                <thead>
                  <tr>
                    <th>품목</th>
                    <th className="qb-right">수량</th>
                    <th className="qb-right">단가</th>
                    <th className="qb-right">금액</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((item, index) => (
                    <tr key={index}>
                      <td>{item.name}</td>
                      <td className="qb-right">{item.quantity}</td>
                      <td className="qb-right">{formatWon(item.unitPrice)}</td>
                      <td className="qb-right">{formatWon(lineAmount(item))}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={3} className="qb-right">
                      공급가액
                    </td>
                    <td className="qb-right">{formatWon(totals.supply)}</td>
                  </tr>
                  <tr>
                    <td colSpan={3} className="qb-right">
                      부가세
                    </td>
                    <td className="qb-right">{formatWon(totals.vat)}</td>
                  </tr>
                  <tr className="qb-total">
                    <td colSpan={3} className="qb-right">
                      합계
                    </td>
                    <td className="qb-right">{formatWon(totals.total)}</td>
                  </tr>
                </tfoot>
              </table>
            )}

            {state.note.trim() === '' ? null : (
              <div className="qb-doc__note">
                <Text variant="label">비고</Text>
                <Text>{state.note}</Text>
              </div>
            )}
          </Card>

          {/* ── 무료/유료 경계 — 손으로 넣는 것은 무료, 사진으로 자동 작성이 구독 ── */}
          <Card className="qb-upsell qb-noprint">
            <Text variant="label">사진으로 자동 작성</Text>
            <Text tone="muted">
              현장 사진과 영수증을 올리면 품목·금액을 자동으로 채워 견적서와 작업완료보고서를 함께 만들어 주는
              것을 준비하고 있습니다. 손으로 넣는 지금 이 도구는 계속 무료입니다.
            </Text>
            <a className="qb-upsell__link" href="/field-docs">
              어떤 도구인지 보기 →
            </a>
          </Card>
        </Container>
      </section>
    </>
  )
}
